/** Generic, server-driven DataGrid Pro wrapper.
 *
 * The hook composes DRF-style URL params (page, page_size, ordering, search)
 * from MUI X's `paginationModel`, `sortModel`, `filterModel` and hands them to
 * `queryFn`. Empty / error / loading states are built in.
 */
import { Alert, Box, type SxProps, type Theme } from '@mui/material';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type DataGridProProps,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridFilterModel,
  type GridPaginationModel,
  type GridRowSelectionModel,
  type GridSlots,
  type GridSortModel,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import type { BatchSelection } from '@/api/batch';
import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import type { CountMode, CountOnlyResult } from '@/api/countStatus';
import type { PaginatedResponse } from '@/api/users';
import { useGridCountMode } from '@/hooks/useUserPreferences';

import { BatchRefusalDialog } from './BatchRefusalDialog';
import type { BatchActionDef, BatchRefusals } from './batchTypes';
import { countLabelDisplayedRows } from './CountTotalLabel';
import { GridControlsToolbar, type GridControlsConfig } from './GridControlsToolbar';
import { GridEmptyState } from './GridEmptyState';
import { SearchTimeoutAlert } from './SearchTimeoutAlert';
import { useBatchAction } from './useBatchAction';
import { useCountMode } from './useCountMode';
import {
  gridIdFromQueryKey,
  gridRefreshToolbar,
  useGridAutoRefresh,
  type GridAutoRefresh,
} from './useGridAutoRefresh';

/** Back-compat alias — pages configure the standard toolbar through this. */
export type HeaderToolbarConfig = GridControlsConfig;

export interface ResourceDataGridParams {
  page: number;
  pageSize: number;
  ordering?: string;
  search?: string;
  extra?: Record<string, string | number | boolean>;
  /** Present when the user chose the audited extended-statement-budget retry
   *  after a `statement_timeout` rejection. Deliberately NOT part of `extra`:
   *  it must not leak into the batch `filterQuery` or invalidate an
   *  "all matching" selection. Pages that set `allowExtendedTimeout` MUST
   *  forward it to the API call (`timeout: params.timeoutTier`). */
  timeoutTier?: 'extended';
  /** Present only when the user has OVERRIDDEN this grid's row-total mode; the
   *  default lives on the backend view, so a cold load deliberately sends
   *  nothing. Kept out of `extra` for the same reason as `timeoutTier`:
   *  flipping the mode must not invalidate an "all matching" selection or leak
   *  into the batch `filterQuery`. Pages that set `countMode` MUST forward it
   *  (`count_mode: params.countMode`). */
  countMode?: CountMode;
}

interface Props<T extends { id: number | string }> {
  queryKey: readonly unknown[];
  queryFn: (params: ResourceDataGridParams) => Promise<PaginatedResponse<T>>;
  columns: GridColDef<T>[];
  defaultPageSize?: number;
  defaultSort?: { field: string; sort: 'asc' | 'desc' };
  checkboxSelection?: boolean;
  onSelectionChange?: (ids: GridRowSelectionModel) => void;
  /** Extra non-grid filters merged into URL params. */
  extra?: Record<string, string | number | boolean>;
  /** Column-header filters: maps a column `field` to the URL param carrying its
   *  value (server decides the match semantics; restrict the column's
   *  `filterOperators` to one operator so the UI doesn't overpromise).
   *  Unmapped columns should set `filterable: false`. */
  filterFieldMap?: Record<string, string>;
  /** Controlled filter model (with fallback: omit both to keep the grid's own
   *  internal filter state). Lets a page persist column filters, e.g. in the
   *  URL query string. */
  filterModel?: GridFilterModel;
  onFilterModelChange?: (model: GridFilterModel) => void;
  /** Controlled sort (with fallback — same pattern as filterModel). Preset-
   *  wired pages own the sort so it can live in the URL / a preset. */
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  /** Controlled column visibility (with fallback to `initialState`). */
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  /** User drag-reordered the columns. Reports the FULL ordered field list
   *  (incl. hidden columns, via gridColumnFieldsSelector) — the grid event's
   *  own indices are visible-relative and unsafe for math. */
  onColumnOrderChange?: (orderedFields: string[]) => void;
  /** Configure the standard toolbar (always rendered — project rule): reset
   *  callback, free-text `search` (only when the backend supports a generic
   *  search param), export file name. Omit for the plain button set. */
  headerToolbar?: HeaderToolbarConfig;
  /** Row click handler receiving the row id. Clicks originating from links /
   *  buttons inside a cell are the caller's concern (stop propagation there). */
  onRowClick?: (id: T['id'], row: T) => void;
  /** Auto-refresh state owned by the PAGE (`useGridAutoRefresh`) — for a page
   *  default (Background Tasks polls from the start) or to keep the chosen
   *  period across a grid remount (loading an uncached preset unmounts the
   *  grid). Omit and the grid owns it: Off on mount, set from the toolbar. */
  autoRefresh?: GridAutoRefresh;
  /** DataGrid initial state — e.g. hide columns by default via
   *  `{ columns: { columnVisibilityModel: { params: false } } }`. */
  initialState?: DataGridProProps['initialState'];
  /** Fixed row height (px) — for multi-line cells. */
  rowHeight?: number;
  /** Enable drag row-reordering (DataGridPro `rowReordering`). Pair with
   *  `onRowOrderChange`; the caller is responsible for only enabling it when a
   *  stable manual order is in effect (e.g. sorted by `sequence`, no filters). */
  rowReordering?: boolean;
  /** Empty-state copy when there are genuinely no records (no filters
   *  active), e.g. "No tours yet." Defaults to "No records." */
  emptyMessage?: string;
  /** Rendered under the empty-state copy — typically the page's Create
   *  button (perm-gated by the caller). Hidden while filters are active. */
  emptyAction?: ReactNode;
  /** Height strategy. 'fill' (default) stretches into the page's flex column
   *  (root the page with PAGE_FILL_SX) so rows virtualize and the grid scrolls
   *  internally — header, toolbar and pager stay visible. 'auto' grows with
   *  content and disables virtualization (embedded tab grids only — pair with
   *  a small `defaultPageSize`). A number is a fixed pixel height. */
  layout?: 'fill' | 'auto' | number;
  /** Fires after a drag-reorder with the FULL new row-id order (the current page
   *  of rows with the move applied) — hand it straight to a `reorder` endpoint. */
  onRowOrderChange?: (orderedIds: T['id'][]) => void;
  /** Styles forwarded to the underlying DataGridPro. */
  sx?: SxProps<Theme>;
  /** Batch-operation actions for the grid's "Batch actions" menu. Supplying a
   *  non-empty array turns on checkbox selection, the SelectAllBanner, and the
   *  toolbar menu. Pair with `batchResource`. Additive: omit for the classic
   *  read-only grid. */
  batchActions?: BatchActionDef[];
  /** URL segment for the generic `/<resource>/batch/` dispatch (e.g. `regions`).
   *  Required for any `batchActions` that use a plain `action` key. */
  batchResource?: string;
  /** Override the query string forwarded to the backend for `all_matching`.
   *  Defaults to the grid's own list params (search + ordering + `extra`), which
   *  is correct whenever `extra` keys are the API param names (the useGridFilters
   *  convention). Supply this for grids whose `extra` keys differ from the API's
   *  (e.g. the blacklist grid remaps them in its queryFn). */
  batchFilterParams?: Record<string, string | number | boolean | undefined>;
  /** The grid's active tenant filter, forwarded to batch dialogs via
   *  `BatchActionContext.tenantFilterId`. Omit on grids with no tenant
   *  dimension (defaults to null = unfiltered). */
  batchTenantFilterId?: number | null;
  /** How a batch dialog reads a row's tenant id (`BatchActionContext.
   *  rowTenantIds`). Defaults to the console-wide `row.tenant?.id`
   *  serializer convention; override for grids that name it differently. */
  batchRowTenantId?: (row: T) => number | null;
  /** Offer "Retry with a longer timeout" when a search is rejected with
   *  `statement_timeout`. Opt-in ONLY for pages whose `queryFn` forwards
   *  `params.timeoutTier` to the API call — otherwise the button would
   *  silently re-run the identical query. */
  allowExtendedTimeout?: boolean;
  /** Enable the row-total mode layer (see `useCountMode`): the pager renders
   *  "of N" / "Show total" instead of a bare number, and the toolbar gains the
   *  mode switch. `grid` is the preference key (use the grid's codename).
   *
   *  The page MUST forward `params.countMode` in its `queryFn`, and should
   *  supply `countOnly` — the `?count_only=1&count_mode=async` call for the
   *  current query — or the "Show total" affordance has nothing to click. */
  countMode?: {
    grid: string;
    countOnly?: (params: ResourceDataGridParams) => Promise<CountOnlyResult>;
  };
  /** The pager's page sizes (default 25 / 50 / 100 / 200). Narrow it for an endpoint that
   *  caps `page_size` lower — a page larger than the cap would skip rows. */
  pageSizeOptions?: number[];
  /** Opt-in: back to page 1 whenever the search, the sort or `extra` changes — in the same
   *  render, so the new query is never asked for the old page (the sync count mode has no
   *  "page past the end" recovery; page 2 of a narrower filter would read as empty). */
  resetPageOnQueryChange?: boolean;
  /** Opt-in: a class per row (DataGridPro `getRowClassName`), e.g. to tint revoked rows. */
  getRowClassName?: DataGridProProps<T>['getRowClassName'];
  /** Opt-in: what a failed load shows instead of the default error Alert (a statement
   *  timeout keeps its own alert) — e.g. an inline message with Retry. */
  renderError?: (error: unknown, retry: () => void) => ReactNode;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

export function ResourceDataGrid<T extends { id: number | string }>({
  queryKey,
  queryFn,
  columns,
  defaultPageSize = 50,
  defaultSort,
  checkboxSelection = false,
  onSelectionChange,
  extra,
  filterFieldMap,
  filterModel,
  onFilterModelChange,
  sortModel,
  onSortModelChange,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  onColumnOrderChange,
  headerToolbar,
  onRowClick,
  autoRefresh: autoRefreshProp,
  initialState,
  rowHeight,
  rowReordering,
  onRowOrderChange,
  emptyMessage = 'No records.',
  emptyAction,
  layout = 'fill',
  sx,
  batchActions,
  batchResource,
  batchFilterParams,
  batchTenantFilterId,
  batchRowTenantId,
  allowExtendedTimeout = false,
  countMode,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  resetPageOnQueryChange = false,
  getRowClassName,
  renderError,
}: Props<T>) {
  const apiRef = useGridApiRef();
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: defaultPageSize,
  });
  const [internalSort, setInternalSort] = useState<GridSortModel>(
    defaultSort ? [{ field: defaultSort.field, sort: defaultSort.sort }] : [],
  );
  const sort = sortModel ?? internalSort;
  const setSort = (m: GridSortModel) => {
    if (onSortModelChange) onSortModelChange(m);
    else setInternalSort(m);
  };
  const [internalFilter, setInternalFilter] = useState<GridFilterModel>({ items: [] });
  const filter = filterModel ?? internalFilter;
  const setFilter = (m: GridFilterModel) => {
    if (onFilterModelChange) onFilterModelChange(m);
    else setInternalFilter(m);
  };

  // One-shot extended statement budget after a `statement_timeout` rejection.
  // Folding it into `params` puts it in the queryKey, so flipping it re-issues
  // the SAME query with the flag — no manual refetch needed.
  const [extendedTimeout, setExtendedTimeout] = useState(false);

  // Row-total mode, part 1: the persisted override. Read here because it has to
  // be in `params` (and therefore the query key) — switching modes must re-issue
  // the query. Part 2 (`useCountMode`) needs the response, so it comes after.
  const countPref = useGridCountMode(countMode?.grid ?? '');
  const countModeParam = countMode ? (countPref.mode ?? undefined) : undefined;

  const params = useMemo<ResourceDataGridParams>(() => {
    const search = filter.quickFilterValues?.join(' ');
    const ordering = sort
      .map((s) => `${s.sort === 'desc' ? '-' : ''}${s.field}`)
      .join(',') || undefined;
    // Column-header filter items → URL params (per filterFieldMap).
    const columnFilters: Record<string, string | number | boolean> = {};
    if (filterFieldMap) {
      for (const item of filter.items) {
        const param = filterFieldMap[item.field];
        if (!param) continue;
        const value = item.value as string | number | boolean | undefined;
        if (value === undefined || value === null || value === '') continue;
        columnFilters[param] = value;
      }
    }
    return {
      page: pagination.page + 1, // DRF is 1-based
      pageSize: pagination.pageSize,
      ordering,
      search,
      extra: { ...extra, ...columnFilters },
      ...(extendedTimeout ? { timeoutTier: 'extended' as const } : {}),
      ...(countModeParam ? { countMode: countModeParam } : {}),
    };
  }, [pagination, sort, filter, extra, filterFieldMap, extendedTimeout, countModeParam]);

  // Opt-in page reset, adjusted during render (React's "storing information from previous
  // renders"): the render with the stale page is discarded before any query runs for it.
  const pageIdentity = JSON.stringify({ s: params.search ?? '', o: params.ordering ?? '', e: params.extra ?? {} });
  const [seenPageIdentity, setSeenPageIdentity] = useState(pageIdentity);
  if (resetPageOnQueryChange && seenPageIdentity !== pageIdentity) {
    setSeenPageIdentity(pageIdentity);
    if (pagination.page !== 0) setPagination((p) => ({ ...p, page: 0 }));
  }

  // Toolbar Refresh + auto-refresh. The grid id (recorded in the audit row) is
  // the page's preset/count-mode codename, else derived from the query key.
  // Polling pauses on the extended-timeout tier: every such request writes its
  // own audit row at the maximum statement budget.
  const ownAutoRefresh = useGridAutoRefresh({
    grid: headerToolbar?.presets?.grid ?? countMode?.grid ?? gridIdFromQueryKey(queryKey),
  });
  const autoRefresh = autoRefreshProp ?? ownAutoRefresh;

  const query = useQuery({
    queryKey: [...queryKey, params],
    queryFn: () => queryFn(params),
    placeholderData: (prev) => prev,
    ...autoRefresh.queryOptions(extendedTimeout),
  });

  // ── Batch operations ─────────────────────────────────────────────────────
  const batchEnabled = Boolean(batchActions && batchActions.length > 0);

  // Controlled selection (fixes the latent "can't clear selection" bug — an
  // uncontrolled grid never re-syncs after we programmatically clear it). Also
  // required so `keepNonExistentRowsSelected` can carry ids across pages.
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [allMatching, setAllMatching] = useState(false);

  // The filter/search/sort identity of the current query — when it changes an
  // "all matching" selection no longer means the same set, so drop it.
  const queryIdentity = useMemo(
    () => JSON.stringify({ s: params.search ?? '', o: params.ordering ?? '', e: params.extra ?? {} }),
    [params.search, params.ordering, params.extra],
  );
  const lastIdentityRef = useRef(queryIdentity);
  useEffect(() => {
    if (lastIdentityRef.current !== queryIdentity) {
      lastIdentityRef.current = queryIdentity;
      if (allMatching) setAllMatching(false);
      // A changed search is a new query — consenting to the extended budget
      // once is not consent to keep burning it on every subsequent search.
      setExtendedTimeout(false);
    }
  }, [queryIdentity, allMatching]);

  // Row-total mode, part 2: everything that needs the response. Without the
  // `countMode` prop this resolves to plain sync behaviour and costs nothing.
  const count = useCountMode({
    pref: countPref,
    queryIdentity,
    response: query.data,
    ...(countMode?.countOnly ? { countOnly: () => countMode.countOnly!(params) } : {}),
    page: pagination.page + 1,
    pageSize: pagination.pageSize,
    ...(query.data ? { rowsOnPage: query.data.results.length } : {}),
    onResetPage: () => setPagination((p) => ({ ...p, page: 0 })),
  });
  /** Exact total when known, else null — never the -1 sentinel. */
  const matchedTotal = countMode ? count.total : (query.data?.count ?? 0);
  const gridRowCount = countMode ? count.rowCount : (query.data?.count ?? 0);

  const selectedIds = useMemo(
    () => selectionModel.map((i) => Number(i)).filter(Number.isFinite),
    [selectionModel],
  );
  // Tenant per selected row, aligned with `selectedIds`. A selected id whose
  // row is NOT on the loaded page (`keepNonExistentRowsSelected`) maps to null
  // — batch dialogs read null as "tenant unknown", never as "tenantless".
  const rowTenantIds = useMemo(() => {
    const readTenant =
      batchRowTenantId ??
      ((row: T): number | null => {
        const tenant = (row as { tenant?: { id?: unknown } | null }).tenant;
        return typeof tenant?.id === 'number' ? tenant.id : null;
      });
    const byId = new Map<number | string, number | null>(
      (query.data?.results ?? []).map((row) => [row.id, readTenant(row)]),
    );
    return selectedIds.map((id) => byId.get(id) ?? null);
  }, [query.data?.results, selectedIds, batchRowTenantId]);
  // The selected rows on the loaded page, for an action's `unavailableReason`.
  const selectedRows = useMemo(() => {
    const picked = new Set<number | string>(selectionModel);
    return (query.data?.results ?? []).filter((row) => picked.has(row.id));
  }, [query.data?.results, selectionModel]);
  // With an unknown total an all-matching selection has no number to show; the
  // batch confirmation still gets the real count from the server.
  const selectedCount = allMatching ? (matchedTotal ?? selectedIds.length) : selectedIds.length;

  const clearSelection = () => {
    setSelectionModel([]);
    setAllMatching(false);
  };

  const handleSelectionChange = (model: GridRowSelectionModel) => {
    setSelectionModel(model);
    // Any manual tick/untick exits all-matching mode (the user is now curating
    // an explicit set again).
    if (allMatching) setAllMatching(false);
    onSelectionChange?.(model);
  };

  // The list query string the backend must re-run to resolve `all_matching`.
  // Default: this grid's own list params (search + ordering + `extra`), which
  // are the API param names under the useGridFilters convention. `page` /
  // `page_size` are omitted — they don't change the matched set.
  const filterQuery = useMemo(() => {
    const usp = new URLSearchParams();
    const source: Record<string, string | number | boolean | undefined> =
      batchFilterParams ?? { search: params.search, ordering: params.ordering, ...params.extra };
    for (const [k, v] of Object.entries(source)) {
      if (v === undefined || v === null || v === '') continue;
      usp.set(k, String(v));
    }
    return usp.toString();
  }, [batchFilterParams, params.search, params.ordering, params.extra]);

  const batchAction = useBatchAction({
    resource: batchResource ?? '',
    queryKey,
    onCleared: clearSelection,
  });

  const runStandard = (
    action: string,
    label: string,
    selection: BatchSelection,
    actionParams?: Record<string, unknown>,
    refusals?: BatchRefusals,
    pinnedQuery?: string,
  ): Promise<void> =>
    batchAction.run({ action, label, selection, params: actionParams, filterQuery: pinnedQuery ?? filterQuery, refusals });

  return (
    <Box
      sx={
        layout === 'auto'
          ? undefined
          : {
              display: 'flex',
              flexDirection: 'column',
              ...(layout === 'fill' ? { flex: 1, minHeight: 360 } : { height: layout }),
            }
      }
    >
      {query.isError &&
        (isStatementTimeout(query.error) ? (
          <SearchTimeoutAlert
            extendedAttempted={extendedTimeout}
            onRetryExtended={
              allowExtendedTimeout && !extendedTimeout
                ? () => setExtendedTimeout(true)
                : undefined
            }
            retrying={query.isFetching}
          />
        ) : renderError ? (
          renderError(query.error, () => void query.refetch())
        ) : (
          <Alert severity="error" sx={{ mb: 2 }}>
            {query.error instanceof ConsoleApiError &&
            (query.error.body as { filters?: string } | undefined)?.filters
              ? (query.error.body as { filters?: string }).filters
              : `Couldn’t load data: ${(query.error as Error).message}`}
          </Alert>
        ))}
      <DataGridPro<T>
        apiRef={apiRef}
        rows={query.data?.results ?? []}
        rowCount={gridRowCount}
        columns={columns}
        loading={query.isLoading || query.isFetching}
        {...(rowHeight ? { rowHeight } : {})}
        {...(getRowClassName ? { getRowClassName } : {})}
        {...(rowReordering ? { rowReordering: true } : {})}
        {...(onRowOrderChange
          ? {
              onRowOrderChange: (p) => {
                const current = query.data?.results ?? [];
                const ids = current.map((r) => r.id);
                const [moved] = ids.splice(p.oldIndex, 1);
                if (moved !== undefined) ids.splice(p.targetIndex, 0, moved);
                onRowOrderChange(ids);
              },
            }
          : {})}
        {...(initialState ? { initialState } : {})}
        {...(columnVisibilityModel !== undefined && { columnVisibilityModel })}
        {...(onColumnVisibilityModelChange && { onColumnVisibilityModelChange })}
        {...(onColumnOrderChange && {
          onColumnOrderChange: () => onColumnOrderChange(gridColumnFieldsSelector(apiRef)),
        })}
        {...(layout === 'auto' ? { autoHeight: true } : {})}
        sx={
          layout === 'auto'
            ? sx
            : [{ flex: 1, minHeight: 0 }, ...(Array.isArray(sx) ? sx : [sx])]
        }
        // DataGridPro does NOT paginate by default (unlike the community grid);
        // without `pagination` it renders a single page with no pager footer.
        pagination
        // Unknown row count (rowCount === -1): `hasNextPage` is what drives the
        // Next button, and a `false` also lets the grid derive the exact total
        // from the current page position — a free total for anyone who pages to
        // the end.
        {...(count.paginationMeta ? { paginationMeta: count.paginationMeta } : {})}
        pageSizeOptions={pageSizeOptions}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        paginationModel={pagination}
        sortModel={sort}
        filterModel={filter}
        onPaginationModelChange={setPagination}
        onSortModelChange={setSort}
        onFilterModelChange={setFilter}
        checkboxSelection={batchEnabled || checkboxSelection}
        {...(batchEnabled
          ? {
              // Controlled selection so we can clear it programmatically and so
              // ticked ids survive pagination (for "select all matching").
              rowSelectionModel: selectionModel,
              onRowSelectionModelChange: handleSelectionChange,
              keepNonExistentRowsSelected: true,
            }
          : { onRowSelectionModelChange: onSelectionChange })}
        {...(onRowClick ? { onRowClick: (p) => onRowClick(p.row.id, p.row) } : {})}
        slots={{
          noRowsOverlay: () => {
            // Distinguish "your filters match nothing" (offer the same reset
            // the toolbar button performs) from a genuinely empty resource.
            const filtersActive =
              filter.items.length > 0 ||
              Boolean(filter.quickFilterValues?.length) ||
              Boolean(headerToolbar?.onResetAllFilters && !headerToolbar.resetDisabled);
            return (
              <GridEmptyState
                filtersActive={filtersActive}
                onClearFilters={() => {
                  setFilter({ items: [], quickFilterValues: [] });
                  headerToolbar?.onResetAllFilters?.();
                }}
                message={emptyMessage}
                action={emptyAction}
              />
            );
          },
          // Standard controls on every grid (project rule). Cast: pnpm gives
          // x-data-grid-pro its own @mui/x-data-grid type instance, so
          // ToolbarPropsOverrides augmentation wouldn't merge.
          toolbar: GridControlsToolbar as GridSlots['toolbar'],
        }}
        slotProps={{
          toolbar: {
            ...(headerToolbar ?? {}),
            refresh: gridRefreshToolbar(query, autoRefresh, { paused: extendedTimeout }),
            ...(countMode ? { countMode: { mode: count.mode, setMode: count.setMode } } : {}),
            ...(batchEnabled && batchActions
              ? {
                  batch: {
                    actions: batchActions,
                    ids: selectedIds,
                    allMatching,
                    count: selectedCount,
                    matched: matchedTotal,
                    filterQuery,
                    tenantFilterId: batchTenantFilterId ?? null,
                    rowTenantIds,
                    rows: selectedRows,
                    runStandard,
                    clearSelection,
                    onSelectAllMatching: () => setAllMatching(true),
                    isPending: batchAction.isPending,
                  },
                }
              : {}),
          } as GridToolbarProps,
          // Skeleton rows on first load; a slim top bar (not a blanking
          // overlay) for background refetches over placeholderData rows —
          // keeps autorefresh grids (bgtasks) from flickering.
          loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
          // "of N" / "Show total" in place of the pager's plain count.
          ...(countMode
            ? {
                pagination: {
                  labelDisplayedRows: countLabelDisplayedRows({
                    state: count.state,
                    total: count.total,
                    canRequest: Boolean(countMode.countOnly),
                    onRequest: count.requestTotal,
                  }),
                },
              }
            : {}),
        }}
      />
      <BatchRefusalDialog refusal={batchAction.refusal} onClose={batchAction.dismissRefusal} />
    </Box>
  );
}
